import React, { useState, useEffect, useCallback } from 'react';
import { CalendarDays, AlertTriangle, Info } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { useCreateAsset, useUpdateAsset } from '../../hooks/useAssets';
import apiClient from '../../config/api';
import { toast } from 'sonner';
import { ASSET_CATEGORIES, OWNERSHIP_CONFIG } from './gearConstants';
import { cn } from '../../lib/utils';

const CATEGORIES = Object.entries(ASSET_CATEGORIES).map(([value, cfg]) => ({
  value,
  label: cfg.label,
  emoji: cfg.emoji,
}));

function toLocalDatetime(date) {
  if (!date) return '';
  const d = new Date(date);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

const INITIAL_STATE = {
  name: '',
  description: '',
  category: 'general',
  ownership: 'owned',
  cost_amount: '',
  cost_currency: 'INR',
  vendor_name: '',
  vendor_contact: '',
  rental_start: '',
  rental_end: '',
};

export default function AssetFormDialog({ open, onClose, filmId, asset }) {
  const createMutation = useCreateAsset();
  const updateMutation = useUpdateAsset();
  const isEditing = !!asset;

  const [form, setForm] = useState(INITIAL_STATE);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!open) return;
    if (asset) {
      setForm({
        name: asset.name || '',
        description: asset.description || '',
        category: asset.category || 'general',
        ownership: asset.ownership || 'owned',
        cost_amount: asset.cost_amount != null ? String(asset.cost_amount) : '',
        cost_currency: asset.cost_currency || 'INR',
        vendor_name: asset.vendor_name || '',
        vendor_contact: asset.vendor_contact || '',
        rental_start: asset.rental_start ? toLocalDatetime(asset.rental_start) : '',
        rental_end: asset.rental_end ? toLocalDatetime(asset.rental_end) : '',
      });
    } else {
      setForm(INITIAL_STATE);
    }
    setErrors({});
  }, [open, asset]);

  const handleChange = useCallback((field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: undefined }));
  }, []);

  const validate = useCallback(() => {
    const errs = {};
    if (!form.name.trim()) errs.name = 'Name is required';
    if (form.cost_amount && (isNaN(Number(form.cost_amount)) || Number(form.cost_amount) < 0)) {
      errs.cost_amount = 'Must be a positive number';
    }
    if (form.ownership === 'rented') {
      if (form.rental_start && form.rental_end && new Date(form.rental_end) < new Date(form.rental_start)) {
        errs.rental_end = 'End must be after start';
      }
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  }, [form]);

  const handleSubmit = async () => {
    if (!validate()) return;

    const payload = {
      name: form.name.trim(),
      description: form.description.trim() || null,
      category: form.category,
      ownership: form.ownership,
      cost_amount: form.cost_amount ? Number(form.cost_amount) : null,
      cost_currency: form.cost_currency || 'INR',
      vendor_name: form.vendor_name.trim() || null,
      vendor_contact: form.vendor_contact.trim() || null,
      rental_start: form.rental_start ? new Date(form.rental_start).toISOString() : null,
      rental_end: form.rental_end ? new Date(form.rental_end).toISOString() : null,
    };

    try {
      if (isEditing) {
        await updateMutation.mutateAsync({ id: asset.id, ...payload });
        toast.success('Gear updated');
        if (isRental && form.rental_start && form.rental_end) {
          toast.info('Pickup & return events updated on calendar', { icon: '📅' });
        }
      } else {
        await createMutation.mutateAsync({ ...payload, film_id: filmId });
        toast.success('Gear added');
        if (isRental && form.rental_start && form.rental_end) {
          toast.info('Pickup & return events auto-created on calendar', { icon: '📅' });
        }
      }
      onClose();
    } catch (err) {
      const detail = err.response?.data?.detail || err.message;
      toast.error(`Failed to ${isEditing ? 'update' : 'add'} gear: ${detail}`);
    }
  };

  const isPending = createMutation.isPending || updateMutation.isPending;
  const isRental = form.ownership === 'rented';

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">
            {isEditing ? 'Edit Gear' : 'Add Gear'}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Name */}
          <div className="space-y-1.5">
            <Label htmlFor="asset-name">Name *</Label>
            <Input
              id="asset-name"
              placeholder="e.g. Sony FX6 Body"
              value={form.name}
              onChange={(e) => handleChange('name', e.target.value)}
              className={errors.name ? 'border-destructive' : ''}
            />
            {errors.name && <p className="text-xs text-destructive">{errors.name}</p>}
          </div>

          {/* Category + Ownership */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Category</Label>
              <Select value={form.category} onValueChange={(v) => handleChange('category', v)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((c) => (
                    <SelectItem key={c.value} value={c.value}>
                      <span className="flex items-center gap-2">
                        <span>{c.emoji}</span>
                        {c.label}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Ownership</Label>
              <Select value={form.ownership} onValueChange={(v) => handleChange('ownership', v)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="owned">Owned</SelectItem>
                  <SelectItem value="rented">Rented</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Description */}
          <div className="space-y-1.5">
            <Label htmlFor="asset-desc">Description</Label>
            <Textarea
              id="asset-desc"
              placeholder="Notes about this equipment..."
              rows={3}
              value={form.description}
              onChange={(e) => handleChange('description', e.target.value)}
            />
          </div>

          {/* Cost */}
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2 space-y-1.5">
              <Label htmlFor="asset-cost">{isRental ? 'Rental Cost / day' : 'Purchase Cost'}</Label>
              <Input
                id="asset-cost"
                type="number"
                min="0"
                step="0.01"
                placeholder="0.00"
                value={form.cost_amount}
                onChange={(e) => handleChange('cost_amount', e.target.value)}
                className={errors.cost_amount ? 'border-destructive' : ''}
              />
              {errors.cost_amount && <p className="text-xs text-destructive">{errors.cost_amount}</p>}
            </div>
            <div className="space-y-1.5">
              <Label>Currency</Label>
              <Select value={form.cost_currency} onValueChange={(v) => handleChange('cost_currency', v)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="INR">INR</SelectItem>
                  <SelectItem value="USD">USD</SelectItem>
                  <SelectItem value="EUR">EUR</SelectItem>
                  <SelectItem value="GBP">GBP</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Vendor info */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="asset-vendor">Vendor Name</Label>
              <Input
                id="asset-vendor"
                placeholder="e.g. Cinekit Rentals"
                value={form.vendor_name}
                onChange={(e) => handleChange('vendor_name', e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="asset-contact">Vendor Contact</Label>
              <Input
                id="asset-contact"
                placeholder="Phone or email"
                value={form.vendor_contact}
                onChange={(e) => handleChange('vendor_contact', e.target.value)}
              />
            </div>
          </div>

          {/* Rental dates (only for rented) */}
          {isRental && (
            <>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="asset-rent-start">Rental Start</Label>
                  <Input
                    id="asset-rent-start"
                    type="datetime-local"
                    value={form.rental_start}
                    onChange={(e) => handleChange('rental_start', e.target.value)}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="asset-rent-end">Rental End</Label>
                  <Input
                    id="asset-rent-end"
                    type="datetime-local"
                    value={form.rental_end}
                    onChange={(e) => handleChange('rental_end', e.target.value)}
                    className={errors.rental_end ? 'border-destructive' : ''}
                  />
                  {errors.rental_end && <p className="text-xs text-destructive">{errors.rental_end}</p>}
                </div>
              </div>

              {/* Auto-event notice */}
              {form.rental_start && form.rental_end && (
                <div className="flex items-start gap-2.5 rounded-lg border border-blue-500/20 bg-blue-500/5 p-3">
                  <CalendarDays className="h-4 w-4 text-blue-500 mt-0.5 shrink-0" />
                  <div className="text-xs text-blue-400">
                    <p className="font-semibold">Calendar events will be auto-created</p>
                    <p className="text-blue-400/70 mt-0.5">
                      A <strong>Pickup</strong> event on {form.rental_start.split('T')[0]} and
                      a <strong>Return</strong> event on {form.rental_end.split('T')[0]} will appear on this film's calendar.
                    </p>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isPending}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={isPending}>
            {isPending
              ? (isEditing ? 'Saving...' : 'Adding...')
              : (isEditing ? 'Save Changes' : 'Add Gear')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
